import { useState, useEffect, useCallback, useMemo } from 'react';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';

import { useAppContext } from '../contextApi/context';
import TableContainer from '../components/table/TableContainer';
import { formatDateAndTime } from '../utils/dates';
import { searchWorkOrders, getMaintenanceTypes } from '../services/OrdersService';
import { searchUsersByName } from '../services/UsersService';
import { exportWorkOrdersReport } from '../services/ExportService';
import { getOrderStatusConfig, ORDER_STATUS } from '../constants/orderStatus';
import { getClientTypeLabel, getClientTypeOptions } from '../constants/orderClientTypes';
import { OrderStatusLegend } from '../styles/StyledComponents';

const ReportWorkOrders = () => {
    // ----- Context
    const { loggedUser } = useAppContext();

    // ----- State
    const [maintenanceTypes, setMaintenanceTypes] = useState([]);
    const [availableUsersForFilter, setAvailableUsersForFilter] = useState([]);
    const [filterTimer, setFilterTimer] = useState(null);

    // ----- Effects
    useEffect(() => {
        const loadMaintenanceTypes = async () => {
            try {
                const types = await getMaintenanceTypes(loggedUser.token);
                setMaintenanceTypes(types);
            } catch (error) {
                console.error('Error al cargar tipos de mantenimiento:', error);
            }
        };

        if (loggedUser?.token) {
            loadMaintenanceTypes();
        }
    }, [loggedUser]);

    // ----- Data Handler
    const fetchWorkOrdersData = useCallback(async (token, page = 1, perPage = 10, sortField = null, filters = []) => {
        try {
            // Transformar filtros del formato del componente al formato de la API
            const filtersData = filters
                .filter((filter) => filter.value !== '' && filter.value !== null && filter.value !== undefined)
                .map((filter) => {
                    let values;

                    // Para filtros de rango de fechas
                    if (filter.operation === 'between' && Array.isArray(filter.value)) {
                        values = filter.value;
                    }
                    // Para filtros múltiples
                    else if (Array.isArray(filter.value)) {
                        values = filter.value;
                    }
                    // Para valores únicos
                    else {
                        values = [filter.value];
                    }

                    // Convertir números para orderNumber
                    if (filter.columnName === 'orderNumber' && values.length > 0) {
                        values = values.map((val) => {
                            const numValue = Number(val);
                            return isNaN(numValue) ? val : numValue;
                        });
                    }

                    return {
                        columnName: filter.columnName,
                        operation: filter.operation,
                        values: values,
                    };
                });

            // Establecer ordenamiento por defecto
            const sortParam = sortField || 'requestedDate,desc';

            // Realizar la llamada a la API
            const response = await searchWorkOrders(token, page, perPage, sortParam, filtersData);

            if (response && response.content) {
                return {
                    content: response.content,
                    totalElements: response.totalElements,
                    currentPage: page,
                    totalPages: response.totalPages,
                    number: response.number,
                    size: response.size,
                    first: response.first,
                    last: response.last,
                };
            } else {
                return {
                    content: [],
                    totalElements: 0,
                    currentPage: page,
                    totalPages: 0,
                };
            }
        } catch (error) {
            console.error('Error al cargar datos de órdenes:', error);
            return {
                content: [],
                totalElements: 0,
                currentPage: page,
                totalPages: 0,
            };
        }
    }, []);

    // ----- Actions
    const handleSearchUsersForFilter = useCallback(
        async (searchText) => {
            if (!searchText || searchText.trim() === '') {
                setAvailableUsersForFilter([]);
                return;
            }

            clearTimeout(filterTimer);

            const newTimer = setTimeout(async () => {
                try {
                    const response = await searchUsersByName(loggedUser.token, searchText);
                    const formattedUsers =
                        response?.map((user) => ({
                            value: user.id,
                            label: `${user.firstName} ${user.lastName} (${user.username})`,
                        })) || [];
                    setAvailableUsersForFilter(formattedUsers);
                } catch (error) {
                    console.error('Error al buscar usuarios:', error);
                    setAvailableUsersForFilter([]);
                }
            }, 500);

            setFilterTimer(newTimer);
        },
        [loggedUser, filterTimer]
    );

    // ----- Helper Functions
    const getOrderNumberWithStatus = (orderNumber, status) => {
        const config = getOrderStatusConfig(status);

        return (
            <div
                className='position-relative'
                style={{
                    paddingLeft: '20px',
                    display: 'inline-block',
                }}
            >
                <OverlayTrigger
                    delay={{ hide: 200, show: 300 }}
                    overlay={(props) => <Tooltip {...props}>Estado: {config.label}</Tooltip>}
                    placement='top'
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '0',
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: config.color,
                            transform: 'translateY(-50%)',
                        }}
                    />
                </OverlayTrigger>
                <span>{orderNumber}</span>
            </div>
        );
    };

    // ----- Constants
    const columns = [
        {
            name: 'N° Orden',
            selector: (row) => getOrderNumberWithStatus(row.orderNumber, row.status),
            sortable: true,
            sortField: 'orderNumber',
            width: '150px',
        },
        {
            name: 'Fecha de solicitud',
            selector: (row) => formatDateAndTime(row.requestedDate),
            sortable: true,
            sortField: 'requestedDate',
            width: '200px',
        },
        {
            name: 'Solicitante',
            selector: (row) => row.requesterUser,
            sortField: 'requesterUser',
            width: '290px',
        },
        {
            name: 'Placa',
            selector: (row) => row.vehiclePlate,
            sortable: true,
            sortField: 'vehiclePlate',
            width: '170px',
        },
        {
            name: 'Tipo de mantenimiento',
            selector: (row) => row.maintenanceTypeName,
            sortable: true,
            sortField: 'maintenanceTypeName',
            width: '230px',
        },
        {
            name: 'Tipo de cliente',
            selector: (row) => getClientTypeLabel(row.orderClientType),
            sortable: true,
            sortField: 'orderClientType',
            width: '150px',
        },
    ];

    // Filtros
    const filters = useMemo(
        () => [
            {
                column: 'orderNumber',
                input: 'text',
                label: 'N° Orden',
                operation: 'equals',
                length: 20,
            },
            {
                column: 'status',
                input: 'select',
                label: 'Estado',
                options: [
                    { value: ORDER_STATUS.CREATED.value, text: ORDER_STATUS.CREATED.label },
                    { value: ORDER_STATUS.APPROVED.value, text: ORDER_STATUS.APPROVED.label },
                    { value: ORDER_STATUS.MAINTENANCE.value, text: ORDER_STATUS.MAINTENANCE.label },
                    { value: ORDER_STATUS.FINALIZED.value, text: ORDER_STATUS.FINALIZED.label },
                    { value: ORDER_STATUS.DISCARDED.value, text: ORDER_STATUS.DISCARDED.label },
                ],
                operation: 'equals',
            },
            {
                column: 'requester.id',
                input: 'autocomplete',
                label: 'Solicitante',
                operation: 'equals',
                placeholder: 'Buscar por nombre, apellido o usuario',
                searchHandler: handleSearchUsersForFilter,
                options: availableUsersForFilter,
                noOptionsText: 'Escribe para buscar',
            },
            {
                column: 'vehicle.plate',
                input: 'text',
                label: 'Placa',
                operation: 'like',
                length: 20,
            },
            {
                column: 'maintenanceType.name',
                input: 'select',
                label: 'Tipo de mantenimiento',
                options: maintenanceTypes.map((type) => ({
                    value: type.name,
                    text: type.name,
                })),
                operation: 'equals',
            },
            {
                column: 'orderClientType',
                input: 'select',
                label: 'Tipo de cliente',
                options: getClientTypeOptions(),
                operation: 'equals',
            },
            {
                column: 'requestedDate',
                input: 'dates',
                label: 'Fecha de solicitud',
                operation: 'between',
            },
        ],
        [maintenanceTypes, availableUsersForFilter, handleSearchUsersForFilter]
    );

    const exportOptions = {
        handler: exportWorkOrdersReport,
    };

    // ----- Render
    return (
        <div className='container mt-5'>
            <h1 className='mb-5'>Reporte de Órdenes de Trabajo</h1>

            {/* Leyenda de estados */}
            <div className='d-flex align-items-center gap-4 mb-3 mt-3' style={{ fontSize: '14px' }}>
                <span className='d-flex align-items-center'>
                    <OrderStatusLegend color={ORDER_STATUS.CREATED.color} />
                    {ORDER_STATUS.CREATED.label}
                </span>
                <span className='d-flex align-items-center'>
                    <OrderStatusLegend color={ORDER_STATUS.APPROVED.color} />
                    {ORDER_STATUS.APPROVED.label}
                </span>
                <span className='d-flex align-items-center'>
                    <OrderStatusLegend color={ORDER_STATUS.MAINTENANCE.color} />
                    {ORDER_STATUS.MAINTENANCE.label}
                </span>
                <span className='d-flex align-items-center'>
                    <OrderStatusLegend color={ORDER_STATUS.FINALIZED.color} />
                    {ORDER_STATUS.FINALIZED.label}
                </span>
                <span className='d-flex align-items-center'>
                    <OrderStatusLegend color={ORDER_STATUS.DISCARDED.color} />
                    {ORDER_STATUS.DISCARDED.label}
                </span>
            </div>

            <TableContainer
                columns={columns}
                fetchDataHandler={fetchWorkOrdersData}
                updateTable={true}
                filters={filters}
                exportOptions={exportOptions}
            />
        </div>
    );
};

export default ReportWorkOrders;
